import round2Decimals from "../Components/round2Decimals";
import round10Decimals from "../Components/round10Decimals";

/*
  Agrupa subtotal/impuestos/retenciones de un arreglo de insumos de Orden de Compra.
  Misma aritmética que ya existía duplicada en la tarjeta de Totales y en onFinish
  de AgregarOrdenDeCompra.js / EditarOrdenDeCompra.js, ahora en un solo lugar.

  ignorarIncompletos:
    - true  (tarjeta de Totales en pantalla): omite filas sin subtotal/total/arrayImpuestos
      calculados todavía (ej. una fila recién agregada sin insumo elegido).
    - false (onFinish, al guardar): no omite ninguna fila — mismo comportamiento que
      siempre tuvo el guardado, no se cambia esa aritmética aquí.
*/
export function calcularTotalesOC(insumos, { ignorarIncompletos = true } = {}) {
  const arrayInsumos = insumos || [];
  let subtotal = 0;
  let total = 0;
  let cuentasProveedor = 0;
  let arrayImpuestos = [];
  let arrayRetenciones = [];

  arrayInsumos.forEach((value) => {
    if (!value) return;
    if (ignorarIncompletos && !(value.subtotal && value.total && value.arrayImpuestos)) return;

    subtotal += value.subtotal;
    total += value.subtotal;

    (value.arrayImpuestos || []).forEach((valueImpuestos) => {
      const index = arrayImpuestos.findIndex(
        (valueImpuestosAnterios) =>
          valueImpuestosAnterios.descripcionFactura === valueImpuestos.descripcionFactura
      );
      if (index !== -1) {
        arrayImpuestos[index] = {
          totalImpuestos: arrayImpuestos[index].totalImpuestos + valueImpuestos.totalImpuestos,
          descripcionFactura: valueImpuestos.descripcionFactura,
        };
      } else {
        arrayImpuestos.push({
          totalImpuestos: valueImpuestos.totalImpuestos,
          descripcionFactura: valueImpuestos.descripcionFactura,
        });
      }
    });

    if (value.arrayRetenciones) {
      value.arrayRetenciones.forEach((valueRetenciones) => {
        const index = arrayRetenciones.findIndex(
          (valueRetencionesAnterios) =>
            valueRetencionesAnterios.descripcionFactura === valueRetenciones.descripcionFactura
        );
        cuentasProveedor += round2Decimals(valueRetenciones.totalRetenciones);
        if (index !== -1) {
          arrayRetenciones[index] = {
            totalRetenciones: arrayRetenciones[index].totalRetenciones + valueRetenciones.totalRetenciones,
            descripcionFactura: valueRetenciones.descripcionFactura,
          };
        } else {
          arrayRetenciones.push({
            totalRetenciones: valueRetenciones.totalRetenciones,
            descripcionFactura: valueRetenciones.descripcionFactura,
          });
        }
      });
    }
  });

  arrayImpuestos = arrayImpuestos.map((valueImpuesto) => {
    total += round2Decimals(valueImpuesto.totalImpuestos);
    return {
      ...valueImpuesto,
      totalImpuestos: round2Decimals(valueImpuesto.totalImpuestos),
    };
  });

  return {
    subtotal,
    total,
    cuentasProveedor,
    totalPagar: total - cuentasProveedor,
    arrayImpuestos,
    arrayRetenciones,
  };
}

/*
  El insumoId de una línea de OC puede ser en realidad el _id de una relación
  (variante por proveedor) y no el del Insumo principal — todasInsumos trae ambos
  mezclados (consultarInsumosYRelaciones en el backend), y las relaciones se
  distinguen por noPrincipal:true + insumoId apuntando al Insumo principal.
  Sin este segundo salto, cualquier insumo recibido vía una relación nunca
  encuentra su configuración de tolerancia (queda como si no tuviera).
*/
export function resolverInsumoMaestroFrontend(insumoIdLinea, todasInsumos) {
  const entrada = (todasInsumos || []).find(
    (v) => v._id?.toString() === insumoIdLinea?.toString()
  );
  if (!entrada) return null;
  if (!entrada.noPrincipal) return entrada;
  return (
    (todasInsumos || []).find((v) => v._id?.toString() === entrada.insumoId?.toString()) || entrada
  );
}

// Estilo del input "Cantidad a recibir" cuando el insumo tiene tolerancia activa —
// tinte azul suave (paleta info de antd) en vez de un bloque sólido.
export const estiloInputConTolerancia = {
  backgroundColor: "#e6f4ff",
  border: "1px solid #91caff",
  borderRadius: 6,
};

/*
  Tope de cantidad tolerado al recibir/facturar una línea de OC, dado el Insumo maestro.
  Espejo de calcularTopeConTolerancia en ordenesDeCompra.controllers.js (backend) —
  el backend es quien realmente bloquea, esto solo mantiene el "Max" del input en pantalla
  coherente con esa validación.
*/
export function calcularTopeConTolerancia(cantidadPedida, insumoMaestro) {
  if (!insumoMaestro?.toleranciaRecepcion) return round10Decimals(cantidadPedida);
  const valorTolerancia = Number(insumoMaestro.valorToleranciaRecepcion) || 0;
  if (insumoMaestro.tipoToleranciaRecepcion === 2) {
    return round10Decimals(cantidadPedida + valorTolerancia);
  }
  return round10Decimals(cantidadPedida * (1 + valorTolerancia / 100));
}

/*
  Familia a la que pertenece una fila de la tabla de insumos de una Orden de Compra,
  para el agrupado por familia de Agregar / Editar OC.

  La familia vive en el insumo, no en la fila: `insumoCompleto.familiaId` +
  `insumoCompleto.familia.nombre`, que es lo que proyectan
  selectInsumosRelacionesProveedor, consultarInsumosRelacionesProveedor y
  resolverClavesProveedorMasivo (backend). Cuando el insumo es una RELACIÓN de
  proveedor, la familia es la del insumo padre — el backend ya la hereda.

  Sin familia se cae a un grupo propio en vez de a null: hay partidas de servicio,
  filas recién agregadas sin insumo elegido y órdenes viejas cuyo insumo ya no
  resuelve, y todas tienen que caber en algún grupo o desaparecerían de la tabla.
*/
export const GRUPO_SIN_FAMILIA = { id: "sin-familia", nombre: "Sin familia" };

export function grupoFamiliaDeFila(fila) {
  const insumo = fila?.insumoCompleto;
  const familiaId = insumo?.familiaId;
  if (!familiaId) return GRUPO_SIN_FAMILIA;
  return {
    id: familiaId.toString(),
    nombre: insumo?.familia?.nombre || "Sin familia",
  };
}
